<template>
  <div class="today-timeline" :data-days-count="daysCount">
    <message
      v-if="lastUpdate && lastDataDownload && lastUpdate > lastDataDownload"
      type="info">
      {{ $t('dashboard.updateRequired') }}
      <a
        v-if="!loadingData"
        href="#"
        @click.prevent="getReservations">
        {{ $t('dashboard.updateData') }}
      </a>
      <a
        v-else
        href="#"
        @click.prevent="false">
        {{ $t('dashboard.updatingData') }}
      </a>
    </message>

    <div
      v-if="loadingData"
      class="today-timeline-empty-state">
      {{ $t('common.ui.loadingData') }}
    </div>

    <div
      v-if="!loadingData && allWorkersHaveFreeDay"
      class="today-timeline-empty-state">
      {{ $t('dashboardTodayTimeline.allWorkersAreFreeToday') }}
    </div>

    <div
      v-if="!allWorkersHaveFreeDay"
      :class="{ 'today-timeline-heading-days': true, 'is-loading': loadingData }">
      <div class="timeline-header">
        <icon
          name="time"
          primaryColor="color-text-light"
          size="m" />
      </div>

      <div
        v-for="(workerData, index) of workerNames"
        :class="{ 'today-timeline-heading-day': true, 'is-hidden': isHiddenCol(index) }"
        :key="'today-timeline-day-header-' + workerData.id">
        <span
          v-if="filteredReservationsData[index] && (Array.isArray(filteredReservationsData[index].hours) || (filteredReservationsData[index].working_hours && !filteredReservationsData[index].working_hours.open))"
          class="today-timeline-heading-day-closed">
          {{ $t('common.ui.freeNoCap') }}
        </span>

        <btn
          v-if="paginationOffset > 0 && parseInt(index, 10) === paginationOffset"
          type="icon"
          class="prev-days"
          iconName="arrow-left-small"
          iconSize="xxs"
          iconColor="color-text-light"
          @click.native="showHiddenWorker(-1)">
        </btn>

        <strong class="today-timeline-heading-day-worker-name">
          {{ workerData.name }}

          <small
            :class="{ 'today-timeline-heading-day-worker-hours': true, 'is-disabled': !workersHoursData }"
            @click.prevent.stop="showInfo('worker-hours-' + workerData.id)">
            {{ $t('dashboardTodayTimeline.checkTimetables') }}
          </small>

          <info-box
            :ref="'worker-hours-' + workerData.id"
            :spacing="72"
            :heightLimit="false"
            position="bottom">
            <div slot="content">
              <div
                v-for="(dayData, index) of workerHours(workerData.id)"
                :class="{ 'worker-hours-day': true, 'is-today': dayIsEqual(dayData.date, today) }"
                :key="'worker-hours-day-' + workerData.id + '-' + index">
                <span :class="{ 'worker-hours-day-name': true, 'is-today': dayIsEqual(dayData.date, today) }">
                  {{ dayData.day_name }}
                </span>

                <span
                  v-for="(hourData, hindex) of dayData.hours"
                  class="worker-hours-day-hours"
                  :key="'worker-hours-day-' + workerData.id + '-' + index + '-' + hindex">
                  <icon
                    name="time"
                    size="xxs"
                    primaryColor="color-text-light" />
                  {{ hourData.from }} - {{ hourData.to }}
                </span>

                <span
                  v-if="dayData.open === 0"
                  class="worker-hours-day-free">
                  {{ $t('common.ui.free') }}
                </span>
              </div>
            </div>
          </info-box>
        </strong>

        <avatar
          :worker="filteredReservationsData[index].worker"
          :name="filteredReservationsData[index].worker.name" />

        <btn
          v-if="paginationOffset < Object.keys(workerNames).length - daysCount && parseInt(index, 10) === (daysCount - 1) + paginationOffset"
          type="icon"
          class="next-days"
          iconName="arrow-right-small"
          iconSize="xxs"
          iconColor="color-text-light"
          @click.native="showHiddenWorker(1)">
        </btn>
      </div>

      <div
        v-for="colIndex of missingCols"
        :class="{ 'today-timeline-heading-day': true }"
        :key="'today-timeline-heading-day-empty-' + colIndex">
      </div>
    </div>

    <div
      v-if="!companyIsClosed && !allWorkersHaveFreeDay"
      :class="{ 'today-timeline-days': true, 'is-loading': loadingData }">
      <div
        v-if="firstWorkerID"
        class="timeline-overlay"
        :style="'height: ' + getTotalTimelineHeight() + 'px;'">
        <span
          v-for="(hourData, index) of reservationsData[firstWorkerID].hours_top"
          :data-hour="hourData.hour"
          :style="'top:' + hourData.top_px + 'px'"
          class="timeline-overlay-line"
          :key="'timeline-overlay-line-' + index">
        </span>
        <span
          v-if="getCurrentTimeProgress() >= 0 && getCurrentTimeProgress() <= 100"
          :style="'top:' + getCurrentTimeProgress() + '%'"
          class="timeline-overlay-line-current">
          <span class="timeline-overlay-line-current-label">{{ $t('dashboardTodayTimeline.now') }}</span>
        </span>
        <span
          v-for="(hourData, index) of reservationsData[firstWorkerID].hours_top"
          :style="'top:' + timelineGetSeparatorTop(index, 1) + 'px'"
          class="timeline-overlay-separator"
          :key="'timeline-overlay-separator-1-' + index">
        </span>
        <span
          v-for="(hourData, index) of reservationsData[firstWorkerID].hours_top"
          :style="'top:' + timelineGetSeparatorTop(index, 2) + 'px'"
          class="timeline-overlay-separator"
          :key="'timeline-overlay-separator-2-' + index">
        </span>
        <span
          v-for="(hourData, index) of reservationsData[firstWorkerID].hours_top"
          :style="'top:' + timelineGetSeparatorTop(index, 3) + 'px'"
          class="timeline-overlay-separator"
          :key="'timeline-overlay-separator-3-' + index">
        </span>
      </div>

      <div
        v-if="firstWorkerID"
        class="timeline-column"
        :style="'height: ' + getTotalTimelineHeight() + 'px;'">
        <span
          v-for="(hourData, index) of reservationsData[firstWorkerID].hours_top"
          :style="'top:' + hourData.top_px + 'px'"
          class="timeline-column-label"
          :key="'timeline-column-hour-' + index">
          {{ hourData.hour }}
        </span>
      </div>

      <div
        v-for="(workerData, columnIndex) of filteredReservationsData"
        :class="{ 'today-timeline-day': true, 'is-closed': Array.isArray(workerData.hours), 'is-hidden': isHiddenCol(columnIndex) }"
        :key="'today-timeline-day-' + columnIndex">
        <div
          v-if="workerData.reservations_number || workerData.blocked_dates_number"
          class="today-timeline-day-head">
          <item-counter
            v-if="peopleNumberEnabled"
            class="people-number"
            icon="profile-big"
            :value="getPeopleNumber(workerData.inquiries)"
            :title="getPeopleNumber(workerData.inquiries) + ' ' + personWordVariations(getPeopleNumber(workerData.inquiries))"
            :text="''"></item-counter>

          <item-counter
            class="reservations-number"
            icon="check-circle"
            :value="workerData.reservations_number"
            :title="workerData.reservations_number + ' ' + reservationWordVariations(workerData.reservations_number)"
            :text="''"></item-counter>

          <item-counter
            class="blocked-dates-number"
            icon="lock"
            :value="workerData.blocked_dates_number"
            :title="workerData.blocked_dates_number + ' ' + blockedDateWordVariations(workerData.blocked_dates_number)"
            :text="''"></item-counter>
        </div>

        <div
          v-else
          class="today-timeline-day-head no-reservations">
          {{ $t('common.ui.noReservations') }}
        </div>

        <div
          v-if="workerData.hours"
          class="today-timeline-day-hours">
          <calendar-item
            v-for="(hourData, hindex) of filteredReservationsData[columnIndex].hours"
            v-if="displayItem(hourData)"
            :key="'timeline-hour-day-' + columnIndex + '-' + hindex"
            :itemData="hourData"
            layout="timeline" />
        </div>

        <div
          :class="{
            'timeline-content': true,
            'is-hovered-timeline': columnIndex === hoveredTimelineColumnIndex
          }"
          :style="'height: ' + getTotalTimelineHeight() + 'px;'"
          @mouseover="hoveredTimelineColumnIndex = columnIndex;"
          @mouseout="hoveredTimelineColumnIndex = false;"
          @mousemove="isDesktop && calculateHourOnMouseMove($event, columnIndex)"
          @click="isDesktop && clickedTimeSlot(columnIndex)">
          <div
            v-for="(block, id) of filteredReservationsData[columnIndex].not_working_blank"
            :key="'not-working-block-' + id"
            :class="{ 'timeline-content-not-working': true, 'timeline-content-not-working-whole-day': filteredReservationsData[columnIndex].working_hours && !filteredReservationsData[columnIndex].working_hours.open }"
            :title="filteredReservationsData[columnIndex].working_hours && filteredReservationsData[columnIndex].working_hours.open ? $t('dashboardTodayTimeline.workerNotWorksInThisHours') : $t('dashboardTodayTimeline.workerIsFree')"
            :style="'top: ' + block.top_px + 'px; height: ' + block.height_px + 'px;'">
          </div>

          <div
            v-if="isDesktop && hoveredTimelineColumnIndex === columnIndex"
            class="timeline-content-helper"
            :data-selected-hour="timelineSelectedHour"
            :style="'height: ' + helperItemHeight() + 'px; top: ' + hoveredTimelineHelperTop + 'px;'">
          </div>

          <div
            v-for="(inquiry, id) in filteredReservationsData[columnIndex].inquiries"
            :key="'inquiry-block-' + id"
            :class="{ 'timeline-content-inquiry': true, 'is-blocking': ['blocking', 'cart', 'company'].indexOf(inquiry.inquiry.type) > -1 }"
            @click.stop="$bus.$emit('show-reservation-details-popup', id, inquiry.inquiry.type)"
            @mouseover.stop
            :style="'background-color: ' + timelineServiceColor(inquiry) + '; top: ' + inquiry.top_px + 'px; left: ' + inquiry.left + '%; width: ' + inquiry.width + '%; height: ' + inquiry.height_px + 'px;'">
            <span
              class="timeline-content-inquiry-details"
              :style="'color: ' + timelineServiceReversedColor(inquiry) + ';'">
              <span
                v-if="timelineInquiryTimeDiff(inquiry) >= 1"
                class="timeline-content-inquiry-time">
                <icon
                  v-if="['blocking', 'cart', 'company'].indexOf(inquiry.inquiry.type) > -1"
                  name="lock"
                  primaryColor="inherit"
                  size="xxxs" />
                {{ timelineInquiryTime(inquiry) }}
              </span>
              <span
                v-if="userCanSeeContactData && timelineInquiryTimeDiff(inquiry) >= 2"
                class="timeline-content-inquiry-name">
                {{ timelineInquiryName(inquiry) }}
              </span>
              <span
                v-if="peopleNumberEnabled && timelineInquiryTimeDiff(inquiry) >= 2 && inquiry.inquiry.people_number"
                class="timeline-content-inquiry-people">
                {{ inquiry.inquiry.people_number }} {{ personWordVariations(inquiry.inquiry.people_number) }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div
        v-for="colIndex of missingCols"
        :class="{ 'today-timeline-day': true, 'is-closed': true }"
        :key="'today-timeline-day-empty-' + colIndex">
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import Avatar from './../common/Avatar';
import CalendarItem from './../ui/CalendarItem';
import CalendarUtils from './../helpers/CalendarUtils';
import Configuration from './../mixins/Configuration';
import ErrorUtils from './../helpers/ErrorUtils.js';
import ItemCounter from './../common/ItemCounter';
import Responsive from './../mixins/Responsive';
import Timeline from './../mixins/Timeline';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'today-timeline',
  mixins: [
    Configuration,
    Responsive,
    Timeline,
    UserTypes,
    WordVariations
  ],
  components: {
    'avatar': Avatar,
    'calendar-item': CalendarItem,
    'item-counter': ItemCounter
  },
  data () {
    return {
      daysCount: 5,
      paginationOffset: 0,
      loadingData: true,
      reservationsData: false,
      workersHoursData: false,
      companyIsClosed: false,
      lastUpdate: null,
      lastDataDownload: null,
      hoveredTimelineColumnIndex: false,
      hoveredTimelineHelperTop: 0,
      timelineSelectedHour: '--:--'
    };
  },
  computed: {
    today () {
      return CalendarUtils.formatDateForPHP(new Date());
    },
    firstWorkerID () {
      let keys = Object.keys(this.reservationsData);
      return keys[0];
    },
    workerNames () {
      if (!this.reservationsData) {
        return {};
      }

      let names = {};
      let IDs = Object.keys(this.reservationsData);

      if (!IDs.length) {
        return names;
      }

      for (let i = 0; i < IDs.length; i++) {
        if (!this.reservationsData[IDs[i]].working_hours.open && !this.reservationsData[IDs[i]].reservations_number) {
          continue;
        }

        names[IDs[i]] = {
          name: this.reservationsData[IDs[i]].worker.name,
          id: this.reservationsData[IDs[i]].worker.id
        };
      }

      return names;
    },
    allWorkersHaveFreeDay () {
      let workingWorkers = Object.keys(this.filteredReservationsData);
      return workingWorkers.length === 0;
    },
    filteredReservationsData () {
      let result = {};

      if (!this.reservationsData) {
        return {};
      }

      let workerIDs = Object.keys(this.reservationsData);

      if (!workerIDs.length) {
        return {};
      }

      for (let i = 0; i < workerIDs.length; i++) {
        if (!this.reservationsData[workerIDs[i]].working_hours.open && !this.reservationsData[workerIDs[i]].reservations_number) {
          continue;
        }

        result[workerIDs[i]] = this.reservationsData[workerIDs[i]];
      }

      return result;
    },
    missingCols () {
      if (!this.reservationsData) {
        return 5;
      }

      let numberOfCols = this.daysCount - Object.keys(this.reservationsData).length;

      if (numberOfCols > 0) {
        return Array.from({length: numberOfCols}, (v, k) => k);
      }

      return [];
    }
  },
  mounted () {
    this.getReservations();
    this.$bus.$on('updated-reservation-status', this.getReservations);
    this.$bus.$on('dashboard-reservations-list-reload-needed', this.getReservations);
    this.$bus.$on('dashboard-reservations-list-tab-today-needs-update', this.updateRequired);
    this.initRWD();
  },
  methods: {
    initRWD () {
      if (window.innerWidth >= 1040 && window.innerWidth < 1240) {
        this.daysCount = 4;
      } else if (window.innerWidth >= 840 && window.innerWidth < 1040) {
        this.daysCount = 3;
      } else if (window.innerWidth >= 540 && window.innerWidth < 840) {
        this.daysCount = 2;
      } else if (window.innerWidth < 540) {
        this.daysCount = 1;
      }

      window.addEventListener('resize', this.onWindowResize);
    },
    onWindowResize () {
      let newDaysCount = this.daysCount;

      if (window.innerWidth >= 1040 && window.innerWidth < 1240) {
        newDaysCount = 4;
      } else if (window.innerWidth >= 840 && window.innerWidth < 1040) {
        newDaysCount = 3;
      } else if (window.innerWidth >= 540 && window.innerWidth < 840) {
        newDaysCount = 2;
      } else if (window.innerWidth < 540) {
        newDaysCount = 1;
      }

      if (newDaysCount !== this.daysCount) {
        this.daysCount = newDaysCount;
        this.loadReservations();
      }
    },
    loadReservations: debounce(function () {
      this.getReservations();
    }, 500),
    getReservations () {
      this.loadingData = true;
      let queryData = this.buildTimelineQuery();

      this.$http.post('/panel/calendar/get-workers-timeline', queryData).then(response => {
        this.companyIsClosed = false;
        this.reservationsData = response.data.timeline;
        this.loadingData = false;
        this.lastDataDownload = +new Date();
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/calendar/get-workers-timeline', this.$router, this.$bus);
        this.loadingData = false;
      });

      this.getWorkersHours();
    },
    getWorkersHours () {
      this.workersHoursData = false;

      this.$http.post('/panel/calendar/get-workers-opening-hours', {
        date: this.today
      }).then(response => {
        this.workersHoursData = response.data.workers;
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/calendar/get-workers-opening-hours', this.$router, this.$bus);
        this.workersHoursData = false;
      });
    },
    buildTimelineQuery () {
      let query = {
        date: this.today
      };

      return query;
    },
    isHiddenCol (workerID) {
      let workerIDs = Object.keys(this.filteredReservationsData);

      if (workerIDs.indexOf(workerID) < this.paginationOffset) {
        return true;
      }

      if (workerIDs.indexOf(workerID) >= this.paginationOffset + this.daysCount) {
        return true;
      }

      return false;
    },
    showHiddenWorker (dir = 1) {
      this.paginationOffset += dir;
    },
    displayItem (hourData) {
      return !this.someFilterIsActive || (this.someFilterIsActive && hourData.reservations);
    },
    showInfo (id) {
      this.$bus.$emit('body-clicked');

      setTimeout(() => {
        this.$refs[id][0].show();
      }, 0);
    },
    workerHours (workerID) {
      if (!this.workersHoursData) {
        return [];
      }

      let result = this.workersHoursData.filter(day => day.id === parseInt(workerID, 10));

      if (!result.length) {
        return [];
      }

      return result[0].days;
    },
    dayIsEqual (dateToCheck, periodDate) {
      let dateFirst = dateToCheck.split('-').map(n => parseInt(n, 10));
      let dateSecond = periodDate.split('-').map(n => parseInt(n, 10));
      let firstTimestamp = new Date(dateFirst[0], dateFirst[1] - 1, dateFirst[2]);
      let secondTimestamp = new Date(dateSecond[0], dateSecond[1] - 1, dateSecond[2]);

      return firstTimestamp.getDay() === secondTimestamp.getDay();
    },
    getPeopleNumber (inquiries) {
      let keys = Object.keys(inquiries);
      let sum = 0;

      for (let i = 0; i < keys.length; i++) {
        sum += inquiries[keys[i]].inquiry.people_number;
      }

      return sum;
    },
    getCurrentTimeProgress () {
      let firstWorker = this.reservationsData[this.firstWorkerID];
      let hours = firstWorker.hours_top;
      let startHour = hours[0].hour.split(':').map(n => parseInt(n, 10));
      let endHour = hours[hours.length - 1].hour.split(':').map(n => parseInt(n, 10));
      let startDateObject = new Date();
      startDateObject.setHours(startHour[0]);
      startDateObject.setMinutes(startHour[1]);
      startDateObject.setSeconds(0);
      startDateObject.setMilliseconds(0);
      let endDateObject = new Date();
      endDateObject.setHours(endHour[0]);
      endDateObject.setMinutes(endHour[1]);
      endDateObject.setSeconds(0);
      endDateObject.setMilliseconds(0);
      let timeDiff = endDateObject.getTime() - startDateObject.getTime();
      let currentTimeDiff = new Date().getTime() - startDateObject.getTime();
      let currentProgress = (currentTimeDiff / timeDiff) * 100;

      return currentProgress;
    },
    updateRequired () {
      this.lastUpdate = +new Date();
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onWindowResize);
    this.$bus.$off('updated-reservation-status', this.getReservations);
    this.$bus.$off('dashboard-reservations-list-reload-needed', this.getReservations);
    this.$bus.$off('dashboard-reservations-list-tab-today-needs-update', this.updateRequired);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.today-timeline {
  background: $color-light;
  border: 1px solid $color-border-light;
  margin: 0 auto 2rem auto;
  max-width: 1400px;
  padding: 0;
  width: 100%;

  &-empty-state {
    color: $color-text-lightest;
    font-size: 2.4rem;
    font-weight: bold;
    padding: 2rem;
    text-align: center;
  }

  &-company-is-closed {
    color: $color-text-lightest;
    font-size: 2.4rem;
    font-weight: bold;
    padding: 2rem 2rem 4rem 2rem;
    text-align: center;
  }

  &-workers-switcher {
    align-items: center;
    color: $color-text-light;
    display: flex;
    padding: 0 0 0 2rem;

    .switcher {
      margin-left: 2rem;
    }
  }

  &-heading {
    align-items: center;
    display: flex;
    padding: 2.5rem;
    position: relative;
    width: 100%;

    h1 {
      font-size: 2.4rem;
      font-weight: 400;
      line-height: 1;
      margin: 0;

      span {
        color: $color-text-light;
      }
    }

    & > .button {
      margin-left: auto;
      margin-right: 0;
    }

    &-days {
      background: $color-bg-medium-alt;
      display: flex;
      position: sticky;
      top: 90px;
      width: 100%;
      will-change: scroll-position;
      z-index: 10;

      &:before {
        background: $color-bg-light;
        content: "";
        height: 25px;
        left: -1px;
        position: absolute;
        top: -25px;
        width: calc(100% + 2px);
      }

      .prev-days,
      .next-days {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 2.4rem;
      }

      .prev-days {
        left: 2.4rem;
      }

      .next-days {
        right: 2.4rem;
      }

      &.is-loading {
        .upcoming-heading-day {
          &-today,
          &-closed {
            display: none;
          }
        }
      }
    }

    &-day {
      align-items: center;
      background: $color-bg-medium;
      display: flex;
      flex: 1;
      flex-wrap: wrap;
      font-size: 1.4rem;
      font-weight: bold;
      justify-content: center;
      height: 7.5rem;
      max-width: 20%;
      position: relative;
      text-align: center;

      &:nth-child(even) {
        background: darken($color-bg-medium, 5%);
      }

      &.is-hidden {
        display: none;
      }

      .avatar {
        left: 50%;
        position: absolute;
        top: 0;
        transform: translateX(-50%) translateY(-50%);
      }

      &-today,
      &-closed {
        background: $color-success;
        color: $color-light;
        border-radius: 1rem;
        font-size: 1.2rem;
        height: 2rem;
        left: 50%;
        line-height: 2rem;
        padding: 0 1rem;
        position: absolute;
        text-transform: uppercase;
        top: 0;
        transform: translateX(-50%) translateY(-50%);
        white-space: nowrap;
      }

      &-closed {
        background: $color-danger;
        top: 100%;
      }

      &-worker {
        position: relative;

        &-name {
          display: block;
          width: 100%;
        }

        &-hours {
          color: $color-notice;
          cursor: pointer;
          display: block;
          font-size: 1.2rem;
          font-weight: normal;
          margin-top: .25rem;
          width: 100%;

          &:hover {
            text-decoration: underline;
          }

          &.is-disabled {
            opacity: .35;
            pointer-events: none;
          }
        }
      }
    }
  }

  &-days {
    display: flex;
    position: relative;
    width: 100%;

    &.is-loading {
      opacity: .25;
    }
  }

  &-day {
    flex: 1;
    max-width: 20%;
    padding: 2rem 2.5rem;

    &:nth-child(even) {
      background: lighten($color-bg-medium, 5%);
    }

    &.is-closed {
      /*
      background: repeating-linear-gradient(
        45deg,
        #f8f8f8,
        #f8f8f8 20px,
        #fff 20px,
        #fff 40px
      );
      */
    }

    &.is-hidden {
      display: none;
    }

    &-head {
      display: flex;
      justify-content: center;

      .people-number,
      .reservations-number {
        margin: 0 .5rem;
      }

      &.no-reservations {
        color: $color-text-light;
        display: block;
        font-size: 1.2rem;
        height: 20px;
        line-height: 1.6;
        padding: 0;
        text-align: center;
      }
    }

    &-hours {
      padding: 2rem 0;
    }

    &.is-closed {
      .upcoming-day-hours-empty-state {
        display: none;
      }
    }
  }

  .info-box {
    & > div > div {
      margin: -.75rem 0;
    }

    .worker-hours-day {
      display: block;
      font-weight: normal;
      margin: .5rem 0 1rem 0;
      position: relative;
      text-align: left;

      &:after {
        border-top: 1px solid $color-border;
        content: "";
        position: absolute;
        top: 8px;
        width: 100%;
      }

      &.is-today {
        border-left: .5rem solid $color-notice;
        margin-left: -2rem;
        padding-left: 1.5rem;
      }

      .worker-hours-day-name {
        background: $color-light;
        display: inline-block;
        font-weight: bold;
        margin-bottom: .25rem;
        padding-right: 1rem;
        position: relative;
        z-index: 1;

        &.is-today {
          color: $color-notice;
        }
      }

      .worker-hours-day-hours {
        display: block;
        padding: .2rem 0;

        .icon {
          margin-right: .5rem;
          position: relative;
          top: 1px;
        }
      }

      .worker-hours-day-free {
        color: $color-danger;
        display: block;
        font-style: italic;
        width: 100%;
      }
    }
  }

  .timeline {
    &-overlay {
      height: 1500px;
      margin-top: 60px;
      pointer-events: none;
      position: absolute;
      width: 100%;

      &-line,
      &-line-current {
        border-top: 1px solid darken($color-border-light, 10%);
        left: 0;
        position: absolute;
        width: 100%;
      }

      &-line-current {
        border-top: 1px solid $color-danger;
        z-index: 1;

        &-label {
          background: $color-danger;
          border-radius: $border-radius-small;
          color: $color-light;
          font-size: 1.1rem;
          font-weight: bold;
          line-height: 1;
          padding: .25rem;
          position: absolute;
          text-align: center;
          text-transform: uppercase;
          top: 0;
          transform: translateY(-50%);
          width: 50px;
          z-index: 1;
        }
      }

      &-separator {
        border-top: 1px dashed darken($color-border, 20%);
        left: 0;
        opacity: .5;
        position: absolute;
        width: 100%;

        &[style^="top: 0%"] {
          display: none;
        }
      }
    }

    &-header {
      align-items: center;
      background-color: $color-bg-lightest;
      border: 1px solid $color-border-light;
      border-left: none;
      display: flex;
      height: 75px;
      justify-content: center;
      width: 50px;
    }

    &-column {
      background-color: $color-bg-lightest;
      border-right: 1px solid $color-border-light;
      height: 1500px;
      margin-top: 60px;
      position: relative;
      width: 50px;

      &:before {
        background-color: $color-bg-lightest;
        border-right: 1px solid $color-border-light;
        content: "";
        display: block;
        height: 60px;
        position: absolute;
        top: -60px;
        width: 50px;
      }

      &-label {
        background: $color-bg-lightest;
        font-size: 13px;
        position: absolute;
        text-align: center;
        transform: translateY(-50%);
        width: 100%;
      }
    }

    &-content {
      height: 1500px;
      margin-top: 21px;
      overflow: hidden;
      position: relative;
    }
  }

  .message.is-info {
    margin: 0 0 2.5rem 0!important;
    max-width: 100%!important;
    width: 100%!important;
  }
}

@media (max-width: 1680px) {
  .today-timeline {
    margin: 0 auto 2rem auto;
    width: 100%;
  }
}

@media (max-width: 1480px) {
  .today-timeline {
    margin: 2rem 0;
    width: 100%;
  }
}

@for $i from 1 through 4 {
  .today-timeline[data-days-count="#{$i}"] {
    .today-timeline-day,
    .today-timeline-heading-day {
      max-width: 100% / $i;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all .25s ease-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: scale(.5);
}
</style>



// WEBPACK FOOTER //
// src/components/ui/DashboardTodayTimeline.vue